import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { CreateInvoiceReq } from '@/services/mobi-api/invoice/types'
import { TextField } from '@mui/material'
import React, { FunctionComponent, useEffect, useReducer } from 'react'
import { useMobiApi } from '@/services/mobi-api'
import { useToast } from '@/context/ToastContext'
import classNames from 'classnames'
import useIntl from '@/utils/intl'
import { Download, ArrowBack } from '@mui/icons-material'
import { useLanguage } from '@/context/LanguageContext'

interface Props {
  onClick: (payload: CreateInvoiceReq) => void
  type: string
}
interface State {
  number?: string
  regNo?: string
  paymentAmount?: string
  numberError?: string
  amountError?: boolean
  checkBill?: boolean
}

const Step1: FunctionComponent<Props> = ({ onClick, type }) => {
  const { locale } = useLanguage()
  const { showError } = useToast()
  const { translate } = useIntl()
  const { bill } = useMobiApi()

  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      number: '',
      regNo: '',
      paymentAmount: ''
    }
  )
  const handleNumber = (value: string): void => {
    if (value.length < 9) {
      setState({
        number: value
      })
    }
    if (value.length === 8) {
      void checkNumber(value)
    }
  }
  const checkNumber = async (number: string): Promise<void> => {
    try {
      const res = await bill.checkNumber(number, type)
      if (res !== undefined) {
        if (res.success) {
          setState({ numberError: undefined })
        }
        if (res?.error !== undefined) {
          setState({ numberError: locale === 'en' ? res.cause?.messageEn ?? translate('error') : res.cause?.messageMn ?? translate('error') })
        }
      } else {
        setState({ numberError: undefined })
      }
    } catch (e) {
      console.error(e)
    }
  }
  const handleRegister = (value: string): void => {
    if (value.length < 11) {
      setState({
        regNo: value
      })
    }
  }
  useEffect(() => {
    if (state.paymentAmount !== '' && state.paymentAmount !== undefined && parseFloat(state.paymentAmount) < 1000) {
      setState({ amountError: true })
    } else {
      setState({ amountError: false })
    }
  }, [state.paymentAmount])

  const handlePayment = (value: string): void => {
    setState({ paymentAmount: value })
  }
  const handleNext = (): void => {
    const payload: CreateInvoiceReq = {
      isdn: state.number,
      type,
      products: [{
        productCode: (state.number ?? '') + ' ' + type,
        productType: 'BILL',
        price: state.paymentAmount !== undefined ? parseFloat(state.paymentAmount) : 0
      }]
    }
    onClick(payload)
  }
  const handleCheckBill = async (): Promise<void> => {
    try {
      if (state.number !== undefined && state.regNo !== undefined) {
        const res = await bill.getInfo(state.number, state.regNo, type)
        if (res !== undefined) {
          if (res.error !== undefined) {
            showError(locale === 'en' ? (res.cause?.messageEn ?? translate('error')) : (res.cause?.messageMn ?? translate('error')))
          }
          setState({
            paymentAmount: res.currentBalance,
            checkBill: false
          })
          if (parseFloat(res.currentBalance) < 1000) {
            setState({ amountError: true })
          }
        } else {
          showError(translate('error'))
        }
      }
    } catch (e) {
      showError(translate('error'))
    }
  }
  if (state.checkBill === true) {
    return (
      <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start overflow-auto relative'>
        <div className='p-4 md:p-8 flex w-full flex-col flex-grow overflow-auto'>
          <TextField
            value={state.number}
            id='outlined-basic' label={type === 'MOBINET_BILL' ? translate('numberInput.hbb') : type === 'VOO_BILL' ? translate('numberInput.voo') : translate('phoneNumber')}
            variant='outlined'
            className='w-full md:w-96 rounded-lg text-textHigh'
            type='number'
            onChange={(e) => { handleNumber(e.target.value) }}
            InputLabelProps={{
              style: { color: 'var(--color-text-textHigh)' }
            }}
            InputProps={{
              style: {
                color: 'var(--color-text-textHigh)',
                '&::placeholder': {
                  color: 'var(--color-text-secondaryHighStroke)'
                }
              } as any
            }}
            sx={{
              '& fieldset': {
                borderRadius: '8px'
              }
            }}
          />
          <p className={classNames('text-sm text-[#FF0000] py-1 pl-2', state.numberError !== undefined ? 'visible' : 'invisible')}>{state.numberError}</p>
          <TextField
            value={state.regNo}
            id='outlined-basic' label={translate('registerNo')} variant='outlined' className='w-full rounded-lg mt-4 text-textHigh' onChange={(e) => handleRegister(e.target.value)}
            InputLabelProps={{
              style: { color: 'var(--color-text-textHigh)' }
            }}
            InputProps={{
              style: {
                color: 'var(--color-text-textHigh)',
                '&::placeholder': {
                  color: 'var(--color-text-secondaryHighStroke)'
                }
              } as any
            }}
            sx={{
              '& fieldset': {
                borderRadius: '8px'
              }
            }}
          />
          <div className='mt-8 flex w-full justify-center'>
            <DarkButton type='outlined' leadingIcon={<ArrowBack fontSize='small' />} size='sm' fullwidth onClick={() => setState({ checkBill: false })}>{translate('bill.back')}</DarkButton>
          </div>
        </div>
        <div className='sticky bottom-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
          <DarkButton disabled={(state.number !== undefined && state.regNo !== undefined) && (state.number?.length < 8 || state.regNo?.length < 10)} fullwidth onClick={() => { void handleCheckBill() }}>{translate('continue')}</DarkButton>
        </div>
      </div>
    )
  }

  return (
    <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start overflow-auto relative'>
      <div className='p-4 md:p-8 flex w-full flex-col flex-grow overflow-auto'>
        <TextField
          value={state.number}
          id='outlined-basic'
          label={type === 'MOBINET_BILL' ? translate('numberInput.hbb') : type === 'VOO_BILL' ? translate('numberInput.voo') : translate('phoneNumber')}
          variant='outlined'
          type='number'
          onChange={(e) => { handleNumber(e.target.value) }}
          className='w-full rounded-lg border-secondaryMainStroke bg-surfaceInverse text-textHigh md:w-96'
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        />
        <p className={classNames('text-sm text-[#FF0000] py-1 pl-2', state.numberError !== undefined ? 'visible' : 'invisible')}>{state.numberError}</p>
        <TextField
          value={state.paymentAmount}
          id='outlined-basic' label={translate('amountDue')} variant='outlined' type='number' onChange={(e) => { handlePayment(e.target.value) }}
          className='w-full rounded-lg border-secondaryMainStroke bg-surfaceInverse text-textHigh md:w-96 mt-4'
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        />
        <p className={classNames('text-sm text-primaryMain py-1 pl-2', state.amountError !== undefined && state.amountError ? 'visible' : 'invisible')}>{translate('bill.warning')}</p>
        {
          type === 'MOBICOM_BILL' && (
            <DarkButton type='outlined' fullwidth leadingIcon={<Download />} size='sm' onClick={() => setState({ checkBill: true })}>{translate('bill.check')}</DarkButton>
          )
        }
      </div>
      <div className='fixed md:sticky bottom-0 right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <DarkButton
          fullwidth
          disabled={state.amountError === true || state.numberError !== undefined || state.number === undefined || state.number.length < 8 || state.paymentAmount === ''}
          onClick={handleNext}
        >
          {translate('continue')}
        </DarkButton>
      </div>
    </div>
  )
}
export default Step1
